import { useEffect, useState } from 'react';
import { paraTela } from '../lib/datas';
import { apiCasos, type Caso } from '../lib/api.ts';
import { completarReais, digitarReais, mascararDinheiro, paraApi } from '../lib/dinheiro.ts';
import { formatarProcesso } from '../lib/rotulos.ts';
import { avisarErro } from './Avisos.tsx';
import { usarEhMaster } from '../lib/papel.ts';
import { Secao } from './Secao.tsx';
import { CampoDoPasso } from './PassoAPasso.tsx';
import '../estilos/formularios-curtos-2026.css';

/**
 * Os cinco marcos da dívida, no topo do relatório.
 *
 * > *"Antes da seção INVESTIGADOS haverá um card com os campos que precisaremos
 * > preencher manualmente: data da inadimplência · data de emissão do
 * > contrato · valor da dívida · nº do processo · data do ajuizamento."*
 * > — Richard, 20/08/2026
 *
 * ## ⚠️ Por que isto é a peça mais importante da abertura
 *
 * **A data da dívida é o EIXO da investigação.** É contra ela que se comparam a
 * transmissão do imóvel, a abertura da empresa e a entrada do sócio novo — e é
 * dessa comparação que sai tudo o que o relatório tem a dizer. Sem estas datas
 * o Hub tem os fatos e não consegue afirmar o que eles indicam; com elas, uma
 * transferência de quotas deixa de ser um fato solto e passa a ter posição na
 * linha do tempo.
 *
 * ⚠️ **As colunas existem desde 19/08/2026 e nunca tiveram onde ser
 * preenchidas.** Foram criadas a pedido da Karen, porque três dos sinais de
 * interposição dependem delas. Até aqui o Hub declarava a lacuna — *"o caso não
 * tem data de dívida; os sinais de tempo não foram avaliados"* — sem oferecer
 * caminho para resolvê-la.
 *
 * ⚠️ **O dado é do CASO, e não do relatório.** Editar daqui grava no caso, e é
 * o mesmo dado que a aba Clientes mostra. Uma cópia no relatório criaria duas
 * verdades sobre a mesma dívida.
 *
 * ⚠️ **`dividaValor` NÃO é dinheiro da KAIRÓS** — é o que o cliente tem a
 * receber do devedor, o tamanho do problema e não o preço do serviço. Por isso
 * ele aparece para todo mundo, como a data: o honorário é que continua morando
 * só no contrato.
 */

/** Grava ao sair do campo, um campo por vez — como as seções do relatório. */
type Campo = 'dividaEm' | 'inadimplenciaEm' | 'ajuizadoEm' | 'processoNumero' | 'dividaValor';

export function MarcosDaDivida({
  caso,
  aoMudar,
}: {
  caso: Caso;
  aoMudar: () => Promise<void>;
}) {
  const [dividaEm, setDividaEm] = useState(caso.dividaEm ?? '');
  const [inadimplenciaEm, setInadimplencia] = useState(caso.inadimplenciaEm ?? '');
  const [ajuizadoEm, setAjuizado] = useState(caso.ajuizadoEm ?? '');
  /* ⚠️ **Guardado só com dígitos, mostrado formatado.** Sem isto, quem digita
     "1234567-89.2025.8.26.0100" recarrega a página e vê
     "12345678920258260100" — um número que ele não reconhece como o que
     escreveu. É a mesma regra do CPF e do CNPJ na casa. */
  const [processo, setProcesso] = useState(formatarProcesso(caso.processoNumero ?? ''));
  const [valor, setValor] = useState(
    caso.dividaValor ? mascararDinheiro(String(Math.round(Number(caso.dividaValor) * 100))) : '',
  );
  const [gravando, setGravando] = useState<Campo | null>(null);
  /*
   * ⛔ **Gravar é do master — 19/09/2026.** A rota é a de editar o caso
   * (`PATCH /api/casos/:id`, `SO_MASTER`), e os campos se abriam para todos: o
   * colaborador digitava, saía do campo e recebia erro. Para ele, os marcos
   * ficam à vista e travados, e a frase diz a quem pedir.
   */
  const ehMaster = usarEhMaster();

  /*
   * O que a seção mostra fechada: os marcos em uma linha.
   *
   * ⚠️ **Vazio some, e é a regra que mais manda aqui.** Nenhum dos cinco é
   * obrigatório — escrever "—" para cada ausente encheria a linha de traços e
   * esconderia o que existe. É a mesma regra do texto de abertura, que omite a
   * frase do marco que falta em vez de escrever "não informado".
   */
  const resumo = (() => {
    const partes = [
      valor ? `R$ ${valor}` : null,
      dividaEm ? `contrato ${paraTela(dividaEm)}` : null,
      inadimplenciaEm ? `inadimplência ${paraTela(inadimplenciaEm)}` : null,
      ajuizadoEm ? `ajuizada ${paraTela(ajuizadoEm)}` : null,
      processo ? `proc. ${processo}` : null,
    ].filter(Boolean);
    /* ⚠️ Card em branco não se anuncia com silêncio: quem chega precisa saber
       que há o que preencher ali dentro. */
    if (partes.length === 0) return <span className="ajuda">nada informado ainda</span>;
    return <span className="resumo-da-divida">{partes.join(' · ')}</span>;
  })();

  /* Quando o caso é recarregado de fora, os campos acompanham. Sem isto, salvar
     um campo deixaria os outros com o valor que estava na tela ao montar. */
  useEffect(() => {
    setDividaEm(caso.dividaEm ?? '');
    setInadimplencia(caso.inadimplenciaEm ?? '');
    setAjuizado(caso.ajuizadoEm ?? '');
    setProcesso(formatarProcesso(caso.processoNumero ?? ''));
    setValor(
      caso.dividaValor ? mascararDinheiro(String(Math.round(Number(caso.dividaValor) * 100))) : '',
    );
  }, [caso]);

  /**
   * ⚠️ **Manda UM campo por vez, e nunca o conjunto.** A rota só grava o que
   * vem no pedido; mandar os cinco a cada saída de campo apagaria o que outra
   * aba tivesse acabado de preencher. É a mesma regra do calendário: mexer só
   * no que se pediu para mexer.
   */
  async function gravar(campo: Campo, valorNovo: string | null) {
    setGravando(campo);
    try {
      await apiCasos.editar(caso.id, { [campo]: valorNovo });
      await aoMudar();
    } catch (e) {
      avisarErro(e);
    } finally {
      setGravando(null);
    }
  }

  return (
    /*
      ⚠️ **Abre e fecha, como as seções — 20/08/2026, a pedido do Richard.**
      Reusa o mesmo `Secao` da ficha do cliente e das seções do relatório: uma
      terceira sanfona divergiria na primeira correção feita só numa delas.

      ⚠️ **O `id` leva o prefixo `rel-`** porque a chave convive no mesmo
      `localStorage` com as da ficha do cliente.
    */
    <Secao
      /* ⚠️ **O `id` NÃO acompanha o título.** Ele é a chave do `localStorage`
         que lembra se o card está aberto ou fechado; trocá-la faria todo mundo
         perder o estado e o card reabrir do zero. Título é rótulo, `id` é
         identidade — e identidade não se troca por causa de rótulo. */
      id="rel-marcos-da-divida"
      /* ⭐ Folha do sistema visual 2026 — 14/09/2026, parte 3. */
      desenho="folha"
      titulo="Sobre a dívida"
      subtitulo="O que o cliente informou sobre a origem do crédito. Todos os campos são opcionais."
      /*
       * ⭐ **O RESUMO na linha do título — 08/09/2026.**
       *
       * > *"Ainda acho muito feio […] toma a tela inteira para poucos campos de
       * > preenchimento."* — Richard
       *
       * ⛔ **O problema não era o estilo dos campos: era o tempo deles na
       * tela.** Estes cinco se preenchem **uma vez**, no começo do caso — e
       * ficavam abertos para sempre, ocupando meia tela em toda visita ao
       * relatório, que é onde ele passa o dia.
       *
       * ⭐ **Fechada, a seção continua dizendo o que importa.** A `Secao` já
       * tinha o `resumo` para isso, e o comentário dela descreve exatamente
       * este caso: *"fechar a seção é justamente quando o número importa"*.
       */
      resumo={resumo}
    >
      <div className="marcos-da-divida">
      <p className="ajuda ajuda-solta">
        <strong>São estas datas que dão sentido a tudo o que vier depois</strong> —
        é contra elas que se compara a transmissão de um imóvel, a abertura de uma
        empresa ou a entrada de um sócio novo.{' '}
        {ehMaster
          ? 'Gravam no caso, e cada campo grava sozinho ao sair dele.'
          : 'Só o master preenche e corrige estes campos; se faltar algum, peça a ele.'}
      </p>

      {/*
        ⭐⭐ **OS AVISOS DE ORDEM DAS DATAS — 11/09/2026.**

        ⛔ **O primeiro caso real tem inadimplência posterior ao ajuizamento**, e
        o Hub carregou as duas para dentro de um prompt, que as devolveu
        afirmadas na mesma frase de um texto assinado. O aviso nasceu **dentro
        do prompt**, e isso não basta: quem pode corrigir é quem digita, e quem
        digita está aqui.

        ⚠️ **A conta é do SERVIDOR** (`datasForaDeOrdem`), a mesma que o prompt
        usa — nunca recalculada na tela. Duas aritméticas para a mesma pergunta
        divergem no primeiro ajuste feito só numa delas.

        ⛔ **Avisa e não bloqueia.** Pode haver explicação — parcela vencida
        depois da ação, dívida renegociada —, e a data continua salvando como
        foi digitada.

        ⚠️ **Só os avisos DESTAS datas.** Os outros do caso (limite de
        investigados, reuniões, assinatura) moram na ficha do cliente, e
        repeti-los aqui poria a mesma frase em duas telas.
      */}
      {caso.avisosDasDatas.length > 0 && (
        <div className="aviso-atencao">
          {caso.avisosDasDatas.map((a) => (
            <p key={a}>{a}</p>
          ))}
        </div>
      )}

      {/* ⭐ No desenho novo desde 18/09/2026: os campos de `CampoDoPasso`, cada
          um dizendo embaixo para que serve — e "gravando…" no lugar da ajuda
          enquanto o campo grava. Continua gravando um campo por vez, ao sair. */}
      <div className="imv-grade-campos fc-divida">
        <CampoDoPasso rotulo="Emissão do contrato" col={2} mono nota={gravando === 'dividaEm' ? 'gravando…' : 'Quando o cliente firmou o contrato com o devedor.'}>
          <input
            id="md-contrato"
            disabled={!ehMaster}
            type="date"
            value={dividaEm}
            onChange={(e) => setDividaEm(e.target.value)}
            onBlur={() => {
              if ((caso.dividaEm ?? '') !== dividaEm) void gravar('dividaEm', dividaEm || null);
            }}
          />
        </CampoDoPasso>
        <CampoDoPasso rotulo="Inadimplência" col={2} mono nota={gravando === 'inadimplenciaEm' ? 'gravando…' : 'Quando o devedor de fato deixou de pagar.'}>
          <input
            id="md-inad"
            disabled={!ehMaster}
            type="date"
            value={inadimplenciaEm}
            onChange={(e) => setInadimplencia(e.target.value)}
            onBlur={() => {
              if ((caso.inadimplenciaEm ?? '') !== inadimplenciaEm) {
                void gravar('inadimplenciaEm', inadimplenciaEm || null);
              }
            }}
          />
        </CampoDoPasso>
        {/*
          ⚠️ **Aparece para todo mundo, inclusive colaborador.** Não é dinheiro
          da KAIRÓS: é o tamanho do problema do cliente, e quem trabalha no
          caso precisa dele como precisa da data. O honorário é que continua
          morando só no contrato.
        */}
        <CampoDoPasso rotulo="Valor da dívida" col={2} mono nota={gravando === 'dividaValor' ? 'gravando…' : 'O risco do cliente com este devedor.'}>
          <i className="imv-pre">R$</i>
          <input
            id="md-valor"
            disabled={!ehMaster}
            inputMode="decimal"
            placeholder="0,00"
            value={valor}
            onChange={(e) => setValor(digitarReais(e.target.value))}
            onBlur={() => {
              // ⭐ Os centavos se completam ao sair (100 → 100,00), antes de comparar e gravar.
              setValor(completarReais(valor));
              const novo = paraApi(valor) || null;
              /*
               * ⚠️ **Compara por NÚMERO, e não por texto.** O banco devolve
               * `850000` e a tela monta `850000.00` — como texto são diferentes,
               * e a comparação crua gravaria a cada vez que o cursor saísse do
               * campo, sem nada ter mudado.
               */
              const antigo = caso.dividaValor;
              const mudou = novo === null || antigo === null ? novo !== antigo : Number(novo) !== Number(antigo);
              if (mudou) void gravar('dividaValor', novo);
            }}
          />
        </CampoDoPasso>
        <CampoDoPasso rotulo="Número do processo" col={4} mono nota={gravando === 'processoNumero' ? 'gravando…' : 'Se já houver ação em tramitação.'}>
          <input
            id="md-proc"
            disabled={!ehMaster}
            inputMode="numeric"
            placeholder="0000000-00.0000.0.00.0000"
            value={processo}
            onChange={(e) => setProcesso(e.target.value)}
            onBlur={() => {
              /* Compara por DÍGITO: o campo mostra formatado e o banco guarda
                 cru, e comparar os dois como texto gravaria a cada saída. */
              const so = processo.replace(/\D/g, '');
              if ((caso.processoNumero ?? '') !== so) void gravar('processoNumero', so || null);
            }}
          />
        </CampoDoPasso>
        <CampoDoPasso rotulo="Ajuizamento" col={2} mono nota={gravando === 'ajuizadoEm' ? 'gravando…' : 'Quando a ação foi proposta, se foi.'}>
          <input
            id="md-ajuiz"
            disabled={!ehMaster}
            type="date"
            value={ajuizadoEm}
            onChange={(e) => setAjuizado(e.target.value)}
            onBlur={() => {
              if ((caso.ajuizadoEm ?? '') !== ajuizadoEm) {
                void gravar('ajuizadoEm', ajuizadoEm || null);
              }
            }}
          />
        </CampoDoPasso>
      </div>

      {/*
        ⚠️ **A lacuna é declarada, e não silenciosa.** Sem nenhuma das três datas
        não há como dizer o que veio antes e o que veio depois — e é disso que
        vive a análise. Calar aqui faria o relatório parecer completo quando
        falta a peça que sustenta o resto.
      */}
      {!dividaEm && !inadimplenciaEm && !ajuizadoEm && (
        <p className="ajuda-alerta ajuda-solta">
          Nenhuma data preenchida. Sem ao menos uma delas, não há contra o que
          comparar as transmissões, as aberturas de empresa e as mudanças
          societárias — e a análise temporal não se sustenta.
        </p>
      )}
      </div>
    </Secao>
  );
}
